import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Avatar,
  Button,
  Modal,
  Table,
  Tabs
} from 'antd';
import {
  viewLeadLogsAtom,
  notificationsAtom,
  addLeadLogAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { toFormattedLocalTime, mapLogType } from '../../../../functions';

function LeadLogsModal() {
  const defaultLogData = {
    logs: [],
    total: 0,
    page: 0,
    size: 20
  };
  const { TabPane } = Tabs;
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useRecoilState(viewLeadLogsAtom);
  const [addLog, setAddLog] = useRecoilState(addLeadLogAtom);
  const [logData, setLogData] = useState(defaultLogData);
  const [automatedLogData, setAutomatedLogData] = useState(defaultLogData);
  const [loading, setLoading] = useState(false);
  const [loadingAutomated, setLoadingAutomated] = useState(false);
  const [selectedTab, setSelectedTab] = useState('1');
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);

  function getLogs(page) {
    setLogData({
      ...logData,
      page
    });
    setLoading(true);
    api.get(`leads/lead/logs/${lead?.id}?page=${page}&size=${logData.size}&automated=false`)
      .then((response) => {
        setLoading(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          setLogData({
            ...logData,
            total: response.data.data.total,
            logs: response.data.data.logs
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function getAutomatedLogs(page) {
    setAutomatedLogData({
      ...logData,
      page
    });
    setLoading(true);
    api.get(`leads/lead/logs/${lead?.id}?page=${page}&size=${logData.size}&automated=true`)
      .then((response) => {
        setLoading(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          setAutomatedLogData({
            ...logData,
            total: response.data.data.total,
            logs: response.data.data.logs
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== logData?.page) {
      getLogs(page - 1);
    } else if (selectedTab === '1') {
      setLogData({
        ...logData,
        size
      });
    } else {
      setAutomatedLogData({
        ...logData,
        size
      });
    }
  };

  useEffect(() => {
    setLogData(defaultLogData);
    setAutomatedLogData(defaultLogData);
    setSelectedTab('1');
    if (lead) {
      setOpen(true);
      getLogs(0);
      getAutomatedLogs(0);
    } else {
      setOpen(false);
    }
  }, [lead]);

  useEffect(() => {
    if (!addLog && lead) {
      if (selectedTab === '1') {
        getLogs(logData?.page);  
      } else {
        getAutomatedLogs(automatedLogData?.page);
      }
    }
  }, [addLog]);

  useEffect(() => {
    if (lead) {
      if (selectedTab === '1') {
        getLogs(logData?.page);  
      } else {
        getAutomatedLogs(automatedLogData?.page);
      }
    }
  }, [logData?.page, automatedLogData?.page]);

  return (
    <Modal
      width="80%"
      title={`${t('notes')} (${logData?.total + automatedLogData?.total})`}
      open={open}
      onCancel={() => {
        setLead(null);
      }}
      footer={[
        <Button
          key="ok"
          type="primary"
          loading={loading}
          onClick={() => setAddLog(lead)}
        >
          {t('add_note')}
        </Button>
      ]}
    >
      <Tabs
        activeKey={selectedTab}
        onChange={(e) => setSelectedTab(e)}
      >
        <TabPane tab={`${t('personal')} (${logData.total})`} key="1">
          <Table
            locale={{
              emptyText: t('no_data')
            }}
            bordered
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: logData?.size,
              showSizeChanger: true,
              current: logData?.page + 1,
              total: logData?.total,
              onChange: onTableChange
            }}
            dataSource={logData?.logs}
            columns={[
              {
                title: '',
                align: 'left',
                className: 'text-sm md:hidden',
                render: (row) => (
                  <div>
                    <p>{row.message}</p>
                    <p className="font-bold mt-4">
                      {t('type')}:
                      <span className="font-normal"> {t(mapLogType(row.type))}</span>
                    </p>
                    <p className="font-bold">
                      {t('created_on')}:
                      <span className="font-normal"> {row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
                    </p>
                    <>
                      <span className="font-bold mt-2">{t('submitted_by')}: </span>
                      {row?.submittedBy && (
                        <span>
                          <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                          {row?.submittedBy?.completeName}
                        </span>
                      )}
                      {!row?.submittedBy && (
                        <span>-</span>
                      )}
                    </>
                  </div>
                ),
              },
              {
                title: t('created_on'),
                align: 'left',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
                ),
              },
              {
                title: t('type'),
                align: 'left',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span> {t(mapLogType(row.type))}</span>
                )
              },
              {
                title: t('message'),
                align: 'left',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{row.message}</span>
                ),
              },
              {
                title: t('submitted_by'),
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <>
                    {row?.submittedBy && (
                      <span>
                        <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                        {row?.submittedBy?.completeName}
                      </span>
                    )}
                    {!row?.submittedBy && (
                      <span>-</span>
                    )}
                  </>
                ),
              },
            ]}
          />
        </TabPane>
        <TabPane tab={`${t('automated')} (${automatedLogData.total})`} key="2">
          <Table
            locale={{
              emptyText: t('no_data')
            }}
            bordered
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: automatedLogData?.size,
              showSizeChanger: true,
              current: automatedLogData?.page + 1,
              total: automatedLogData?.total,
              onChange: onTableChange
            }}
            dataSource={automatedLogData?.logs}
            columns={[
              {
                title: '',
                align: 'left',
                className: 'text-sm md:hidden',
                render: (row) => (
                  <div>
                    <p>{row.message}</p>
                    <p className="font-bold mt-4">
                      {t('type')}:
                      <span className="font-normal"> {t(mapLogType(row.type))}</span>
                    </p>
                    <p className="font-bold">
                      {t('created_on')}:
                      <span className="font-normal"> {row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
                    </p>
                    <>
                      <span className="font-bold mt-2">{t('submitted_by')}: </span>
                      {row?.submittedBy && (
                        <span>
                          <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                          {row?.submittedBy?.completeName}
                        </span>
                      )}
                      {!row?.submittedBy && (
                        <span>-</span>
                      )}
                    </>
                  </div>
                ),
              },
              {
                title: t('created_on'),
                align: 'left',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
                ),
              },
              {
                title: t('type'),
                align: 'left',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span> {t(mapLogType(row.type))}</span>
                )
              },
              {
                title: t('message'),
                align: 'left',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{row.message}</span>
                ),
              },
              {
                title: t('submitted_by'),
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <>
                    {row?.submittedBy && (
                      <span>
                        <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                        {row?.submittedBy?.completeName}
                      </span>
                    )}
                    {!row?.submittedBy && (
                      <span>-</span>
                    )}
                  </>
                ),
              },
            ]}
          />
        </TabPane>
      </Tabs>
    </Modal>
  );
}

export default LeadLogsModal;
